import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Upload, Check, AlertCircle } from "lucide-react";
import { useAppServices } from "../../../hook/services";
import { Link as LinkType } from "../../../types";
import Button from "../../../components/ui/Button";
import HelpHint from "../../../components/ui/HelpHint";

interface Props {
  open: boolean;
  onClose: () => void;
  // Created links are handed back so the list can prepend them.
  onCreated: (links: LinkType[]) => void;
}

interface ParsedItem {
  url: string;
  title?: string;
}

// Parse pasted text / CSV. One link per line; an optional comma splits
// "url, title" (or "title, url" — whichever looks like the URL wins).
const parseLines = (text: string): { items: ParsedItem[]; invalid: number } => {
  const items: ParsedItem[] = [];
  let invalid = 0;
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const parts = line.split(",").map((p) => p.trim());
    const urlPart = parts.find((p) => /^https?:\/\//i.test(p));
    if (!urlPart) {
      invalid += 1;
      continue;
    }
    const title = parts.filter((p) => p !== urlPart).join(", ") || undefined;
    items.push({ url: urlPart, title });
  }
  return { items, invalid };
};

const BulkImportModal = ({ open, onClose, onCreated }: Props) => {
  const services = useAppServices();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    createdCount: number;
    invalid: number;
    overflow: number;
    unsafe: number;
  } | null>(null);

  const { items, invalid } = useMemo(() => parseLines(text), [text]);

  const reset = () => {
    setText("");
    setError(null);
    setResult(null);
  };

  const handleImport = async () => {
    if (!items.length) return;
    setBusy(true);
    setError(null);
    const { response, error: err } = await services.url_tracker.bulkCreate({
      payload: { items },
    });
    setBusy(false);
    if (err) {
      setError(err.message || "Import failed.");
      return;
    }
    if (response) {
      if (Array.isArray(response.created)) onCreated(response.created);
      setResult({
        createdCount: response.createdCount || 0,
        invalid: (response.invalid || 0) + invalid,
        overflow: response.overflow || 0,
        unsafe: response.unsafe || 0,
      });
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 backdrop-blur-sm p-4 anim-in overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden my-auto">
        <div className="relative px-6 pt-6 pb-4 border-b border-ink-100">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center shadow-sm">
              <Upload size={18} className="text-white" />
            </span>
            <div>
              <h2 className="font-display text-base font-semibold text-ink-900 tracking-tight flex items-center">
                Bulk import links
                <HelpHint title="Bulk import" anchor="links-bulk">
                  Paste or upload a CSV of destination URLs to create many
                  tracked links at once. Each destination is safety-checked, and
                  anything flagged is reported back.
                </HelpHint>
              </h2>
              <p className="text-xs text-ink-500 mt-0.5">
                Paste one URL per line. Add a title with{" "}
                <code className="font-mono text-ink-600">url, Title</code>.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute top-5 right-5 text-ink-400 hover:text-ink-700 hover:bg-ink-100 w-8 h-8 rounded-lg flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5">
          {result ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
                <Check size={22} strokeWidth={2.5} />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-ink-900">
                Imported {result.createdCount} link
                {result.createdCount === 1 ? "" : "s"}
              </h3>
              {(result.invalid > 0 || result.overflow > 0 || result.unsafe > 0) && (
                <p className="mt-2 text-sm text-ink-500">
                  {result.invalid > 0 && (
                    <>{result.invalid} line(s) skipped (not a valid URL). </>
                  )}
                  {result.unsafe > 0 && (
                    <>{result.unsafe} skipped — flagged as unsafe. </>
                  )}
                  {result.overflow > 0 && (
                    <>{result.overflow} skipped — plan limit reached.</>
                  )}
                </p>
              )}
              <div className="mt-5 flex items-center justify-center gap-2">
                <Button variant="secondary" size="sm" onClick={reset}>
                  Import more
                </Button>
                <Button size="sm" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-3">
                  <AlertCircle size={15} className="shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={9}
                placeholder={
                  "https://example.com/page-one\nhttps://example.com/sale, Spring Sale\nhttps://example.com/promo, Promo banner"
                }
                className="w-full px-3 py-2 bg-white border border-ink-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 resize-none placeholder:text-ink-300"
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-ink-500">
                  {items.length} valid link{items.length === 1 ? "" : "s"}
                  {invalid > 0 && (
                    <span className="text-amber-600"> · {invalid} skipped</span>
                  )}
                </span>
                <Button
                  onClick={handleImport}
                  loading={busy}
                  disabled={!items.length || busy}
                  icon={!busy ? <Upload size={14} /> : undefined}
                >
                  Import {items.length || ""}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BulkImportModal;
